<template>
    <data v-if="user">
        <!-- several servers down at once (ProxyMonitor): one notice for everyone -->
        <div v-if="outages.active_servers >= 3 && hasProxyDown" class="outage-notice" role="status">
            <v-icon icon="mdi-alert-outline" size="20"></v-icon>
            <div><b>{{ t('outage.siteTitle', { n: outages.active_servers }) }}</b> {{ t('outage.siteText') }}</div>
        </div>
        <!-- recommended anti-detect partner; closes with x (remembered in this browser for this account) -->
        <div v-if="showPartner && PARTNERS_ON" class="partner-banner">
            <span class="partner-banner__mark" aria-hidden="true">U</span>
            <div class="partner-banner__txt">
                <b>{{ t('partner.bannerTitle') }}</b>
                <span>{{ t('partner.bannerText') }}</span>
            </div>
            <a class="partner-banner__btn" :href="UNDETECTABLE.url" target="_blank" rel="sponsored noopener">{{ t('partner.try') }} ↗</a>
            <button type="button" class="partner-banner__x" :aria-label="t('partner.close')" @click="hidePartner">×</button>
        </div>
        <div class="flex justify-between items-center pb-4">
        <div class="flex flex-wrap">
            <div class="w-56 pr-5 text-xs text-black">
                <p class='py-1'>{{ t('myproxy.selectStatus') }}</p>

                <v-select v-model="selectedLicenseStatus" :items="licenseStatusItems" density="compact"
                    variant="outlined" :placeholder="t('myproxy.selectPlaceholder')" rounded="0"
                    style="padding-top: 0px;padding-bottom: 0px;" class="bg-white text-black">

                </v-select>
            </div>
            <div class="w-56 pr-5 text-xs text-black">
                <p class='py-1'>{{ t('myproxy.selectView') }}</p>

                <v-select v-model="selectedView" :items="viewItems" density="compact"
                    variant="outlined" :placeholder="t('myproxy.selectPlaceholder')" rounded="0"
                    style="padding-top: 0px;padding-bottom: 0px;" class="bg-white text-black">

                </v-select>
            </div>
            <!-- export the active proxies as a spreadsheet (CSV) -->
            <div class="pr-5 text-xs text-black flex items-end">
                <v-btn size="small" class="copy-all-btn mb-[2px]" prepend-icon="mdi-download" :title="t('myproxy.exportCsvSub')" @click="exportProxies">{{ t('myproxy.exportBtn') }}</v-btn>
            </div>
            <div @click="toggleSelection()" class="ml-auto align-self-center flex flex-row mt-[20px] cursor-pointer">
                <input :checked="expiringIn" class="form-check-input shadow-none cursor-pointer mr-[5px]" type="checkbox" />
                <span>{{ t('myproxy.expiring24') }}</span>
            </div>
        </div>
      </div>
        <div v-if="loading === true" class=" flex items-center justify-center absolute w-full h-1/3 custom-style " >
            <img src="../../../assets/loading.gif" class="w-[50px]"/>
        </div>
   
      <div v-if="selectedView === 'Table'" class="px-4 py-4 bg-white">
          
          <v-data-table :headers="headers" items-per-page="-1"
              :items="visibleProxies"
              item-key="license_uuid" class=" bg-gray border text-black min-h-[500px]" style="font-size: 13px;" :style="`height: ${computedHeight}px;`">

              <template v-slot:[`item.no`]="{ index }">
                  {{ index + 1 }}
              </template>
              <template v-slot:[`item.note`]="{item}">
                <div class="flex flex-row text-sm ">
                    <div v-if="item.note" class="flex justify-between items-center bg-white px-3 w-[150px] py-1 text-black">
                        <pre class="text-green-600 font-bold w-full">{{item.note}}</pre>
                    </div>
                    <div class="flex flex-row items-center">
                        <v-icon v-if="item.note" icon="mdi-square-edit-outline"  class="cursor-pointer my-[1px] mx-4"
                            data-bs-toggle="modal" color="light-green mx-1"
                            data-bs-target="#noteModal"
                            @click="proxyAction(item, 'UPDATENOTE')"
                        ></v-icon>
                        <v-icon v-else icon="mdi-plus-thick"  class="cursor-pointer my-[1px] mx-4"
                            data-bs-toggle="modal" color="light-green mx-1"
                            data-bs-target="#noteModal"
                            @click="proxyAction(item, 'ADDNOTE')"
                        ></v-icon>
                    </div>
                  </div>
              </template>
              <template v-slot:[`item.coupon_code`]="{item}">
                <div class="flex flex-row text-sm ">
                    <div class="pl-4 text-green-600">{{item?.coupon_code}}</div>
                    <v-icon v-if="item?.coupon_code" icon="mdi-square-edit-outline"  class="cursor-pointer my-[1px] mx-4"
                            data-bs-toggle="modal" color="light-green mx-1"
                            data-bs-target="#couponModal"
                            @click="proxyAction(item, 'EDITCOUPON')"
                    ></v-icon>
                    <v-icon v-else icon="mdi-plus-thick"  class="cursor-pointer my-[1px] mx-4"
                        data-bs-toggle="modal" color="light-green mx-1"
                        data-bs-target="#couponModal"
                        @click="proxyAction(item, 'EDITCOUPON')"
                    ></v-icon>
                </div>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                  <div class="space-y-1 py-1 font-semibold ">
                      <div class="flex justify-between items-center bg-white px-3 py-1 text-black">
                          {{ t('myproxy.serverOnline') }} <v-icon icon="mdi-circle" size="15"
                              :color="(item.status_server_online ?? item.modem_conn_status) ? 'green' : 'red'" class="mr-2"></v-icon>
                      </div>
                      <div v-if="isExpired(item)" class="expired-tag">{{ t('myproxy.expiredTag') }}</div>
                      <div v-else class="flex justify-between items-center bg-white px-3 py-1 text-black" :title="connsTitle(item)">
                          {{ proxyStateText(item) }} <v-icon icon="mdi-circle" size="15"
                              :color="shownStatus(item).color" class="mr-2"></v-icon>
                      </div>
                  </div>
              </template>
              <template v-slot:[`item.expired_date`]="{ item }">
                  <div v-if="isExpired(item)" class="text-red-600 font-bold">{{ t('myproxy.expiredLabel') }} {{ formatDate(new Date(item.expired_date)) }}</div>
                  <div v-else-if="calculateRemainingTime(item.expired_date).isOne" class="text-red-600 font-bold">{{ t('myproxy.remainingValue', { time: calculateRemainingTime(item.expired_date).time }) }}</div>
                  <div v-else class="text-green-600 font-bold">{{ t('myproxy.remainingValue', { time: calculateRemainingTime(item.expired_date).time }) }}</div>
                  <div>{{ t('myproxy.start', { date: formatDate(new Date(item.start_date)) }) }}</div>
                  <div>{{ t('myproxy.expire', { date: formatDate(new Date(item.expired_date)) }) }}</div>
              </template>
              <template v-slot:[`item.proxy_ports`]="{ item }">
                  <div v-if="outageOf(item)" class="outage-ban" :class="outageOf(item).state === 'outage' ? 'is-out' : 'is-ok'" role="status">
                      <v-icon :icon="outageOf(item).state === 'outage' ? 'mdi-lan-disconnect' : 'mdi-check-circle-outline'" size="18"></v-icon>
                      <span v-if="outageOf(item).state === 'outage'"><b>{{ t('outage.offlineTitle') }}</b> {{ t('outage.offlineText', { time: outageTime(outageOf(item).since) }) }}</span>
                      <span v-else><b>{{ t('outage.backTitle') }}</b> {{ t('outage.backText', { min: outageOf(item).minutes }) }}</span>
                  </div>
                  <ProxyConnDetails :proxy="item" :expired="isExpired(item)" :copy-all="proxyDetails(item)" @edit-auth="proxyAction(item, 'AUTHENTICATION')" />
              </template>
              <template v-slot:[`item.ip_status`]="{ item }">
                  <div class="flex flex-col">
                      <div
                          v-if="!isIpv6Proxy(item)"
                          :class="ipStatus[item.uuid] ? 'text-light-green' : 'text-red'"
                          class="font-bold flex justify-center items-center">
                          <div>{{ t('myproxy.modemIpv4Value', { ip: item.public_ip ? item.public_ip : '* . * . * . * ' }) }}</div>
                          <v-btn density="compact" variant="text" icon="mdi-refresh" color="light-green"
                              @click="refreshIp(item.uuid)"></v-btn>
                      </div>
                      <div
                          v-if="isIpv6Proxy(item)"
                          :class="ipStatus[item.uuid] ? 'text-light-green' : 'text-red'"
                          class="text-[11px] text-center"
                      >
                          {{ t('myproxy.modemIpv6Value', { ip: item.public_ipv6 ? item.public_ipv6 : t('common.pendingSync') }) }}
                          <v-btn density="compact" variant="text" icon="mdi-refresh" color="light-green"
                              @click="refreshIp(item.uuid)"></v-btn>
                      </div>
                      <v-btn :disabled="!ipStatus[item.uuid]" class="font-bold bg-dark text-light" variant="outliend"
                          style="outline:none; font-size: 12px;" density="compact" @click="changeIp(item.uuid)">
                          {{ t('common.changeIp') }}
                      </v-btn>
                      <div v-if="item.m_type === 'PROXYSMART'" :style="checkDownloadLimit(item, item.dl_bytes) ? 'color: red' : ''">
                      {{ item.dl_bytes && item.dl_bytes !== "NaN" ? item.dl_bytes : '0MB'}} 
                      </div>
                      <div v-else-if="item.m_type === 'PROPROXYUI'" :style="ppOver(item) ? 'color: red' : ''">
                          {{ t('myproxy.ppToday', { used: item.dl_bytes }) }}<span v-if="item.daily_gb > 0">&nbsp;{{ t('myproxy.ppOfGb', { gb: item.daily_gb }) }}</span>
                      </div>
                      <div v-else-if="item.m_type === 'X_PROXY' && item.m_variant === 'v2'" :style="checkDownloadLimit(item, item.dl_bytes) ? 'color: red' : ''">
                          HTTP+SOCKS5: {{ item.dl_bytes }} MB
                      </div>
                      <div v-else-if="item.m_type === 'X_PROXY'">
                          <div :style="checkDownloadLimit(item, item.dl_bytes) ? 'color: red' : ''">
                              HTTP: 
                              {{ item.dl_bytes }} MB
                          </div>
                          <div :style="checkDownloadLimit(item, (item.counter_dl_used_bytes / (1024 * 1024))) ? 'color: red' : ''">
                              SOCKS5:  
                              {{ (item.counter_dl_used_bytes / (1024 * 1024)).toFixed(2) }} MB
                          </div>
                      </div>
                  </div>
              </template>
              <template v-slot:[`item.buttonBar`]="{ item }">
                  <v-menu offset-y location="bottom">
                      <template v-slot:activator="{ props, on, isActive }">
                          <v-btn v-bind="props" v-on="{ ...on }" density="compact"
                              :style="isActive ? 'border-bottom-left-radius: 0px;border-bottom-right-radius: 0px;' : ''"
                              style="outline: none; font-size: 10px; " class="w-32 z-10 bg-dark text-light">
                              {{ t('myproxy.actions') }}
                              <v-icon icon="mdi-chevron-down" size="14"></v-icon>
                          </v-btn>
                      </template>
                      <v-list class="w-32 flex-1 flex flex-col px-0 py-0  z-0"
                          style="border-top-left-radius: 0px;border-top-right-radius: 0px">

                          <template v-for="(button, index) in buttonsFor(item)" :key="index">
                              <!-- Check if button is "abc" -->

                              <v-btn @click="proxyAction(item, button)" :disabled="isExpired(item) && !ALLOWED_WHEN_EXPIRED.includes(button)" :class="{ 'btn-extend': isExpired(item) && button === 'EXTEND', 'btn-os-on': button === 'OS Spoofing' && osProfile(item) }"
                                  rounded="0"
                                  class="bg-dark text-light text-center "
                                  style="font-size: 10px; width: 128px; outline: none; border-bottom: 1px solid #345;"
                                  type="button"
                                  :data-bs-toggle="(button === 'AUTO ROTATION' || button === 'AUTHENTICATION' || button === 'EXTEND' || button === 'OS Spoofing' || button === 'IP HISTORY' || button === 'VPN' || button === 'VPN ✓') && 'modal'"
                                  :data-bs-target="button === 'AUTO ROTATION' ? '#autoRotationDialog' :
      button === 'AUTHENTICATION' ? '#authenticationModal' :
          button === 'EXTEND' ? '#extendLicenseModal' : button === 'OS Spoofing' ? '#osSpoofingModal' :
            button === 'IP HISTORY' ? '#ipChangeHistoryModal' : (button === 'VPN' || button === 'VPN ✓') ? '#vpnModal' :
              ''">
                                  {{ buttonLabel(button, item) }}
                              </v-btn>
                              
                          </template>
                      </v-list>
                  </v-menu>
              </template>
              <template v-slot:[`item.auto_extend`]="{ item }" >
                  <div class="flex flex-col">
                      <div class="flex justify-center my-1">
                          <v-switch density="dense" color="light-green" v-model="item.auto_extend" label-placement="left"
                              label="" @click="updateAutoExtend(item)"></v-switch>
                      </div>
                      {{ item.auto_rotation_time <= 0 ? t('common.notDefined') : t('common.minutes', { n: item.auto_rotation_time }) }}
                  </div>
              </template>
              <template v-slot:[`item.auth`]="{ item }">
                <div class="flex flex-col gap-2 py-1">
                  <div v-if="item.os_spoofing && osProfile(item)" class="text-start"><span class="os-chip" :title="osDescription(item)">{{ t('myproxy.osActive', { os: osProfile(item) }) }}</span></div>
                  <!-- ProProxyUI: WireGuard / OpenVPN files and whether they are connected -->
                  <div v-if="item.m_type === 'PROPROXYUI' && (item.vpn_wg || item.vpn_ovpn) && !isExpired(item)" class="vpn-stack">
                              <div v-if="item.vpn_wg" class="vpn-item" title="Download the WireGuard file">
                                  <img :src="wireGuardLogo" alt="WireGuard" class="vpn-logo vpn-logo--wg cursor-pointer" @click="downloadVpn(item, 'wg')"/>
                                  <div class="vpn-item__text">
                                      <span class="vpn-item__name">WireGuard <button type="button" class="vpn-dl" @click="downloadVpn(item, 'wg')"><v-icon icon="mdi-download" size="11"></v-icon>{{ t('myproxy.vpnDownload') }}</button><button type="button" class="vpn-dl" @click="showVpnQr(item, 'wg')"><v-icon icon="mdi-qrcode" size="11"></v-icon>QR</button></span>
                                      <span v-if="item.vpn" class="vpn-state" :class="{ 'is-on': item.vpn.wg_connected }"><i></i>{{ item.vpn.wg_connected ? t('myproxy.vpnOn') : t('myproxy.vpnOff') }}</span>
                                  </div>
                              </div>
                              <div v-if="item.vpn_ovpn" class="vpn-item" title="Download the OpenVPN file">
                                  <img :src="openVpnLogo" alt="OpenVPN" class="vpn-logo cursor-pointer" @click="downloadVpn(item, 'ovpn')"/>
                                  <div class="vpn-item__text">
                                      <span class="vpn-item__name">OpenVPN <button type="button" class="vpn-dl" @click="downloadVpn(item, 'ovpn')"><v-icon icon="mdi-download" size="11"></v-icon>{{ t('myproxy.vpnDownload') }}</button><button type="button" class="vpn-dl" @click="showVpnQr(item, 'ovpn')"><v-icon icon="mdi-qrcode" size="11"></v-icon>QR</button></span>
                                      <span v-if="item.vpn" class="vpn-state" :class="{ 'is-on': item.vpn.ovpn_connected }"><i></i>{{ item.vpn.ovpn_connected ? t('myproxy.vpnOn') : t('myproxy.vpnOff') }}</span>
                                  </div>
                              </div>
                  </div>
                  <!-- ProxySmart: OpenVPN file and QR code -->
                  <div v-if="item.m_type === 'PROXYSMART' && item.openvpn_available !== false && !isExpired(item)" class="vpn-stack">
                              <div class="vpn-item" title="Download the OpenVPN file">
                                  <img :src="openVpnLogo" alt="OpenVPN" class="vpn-logo cursor-pointer" @click="downloadFile(openVpnLink(item))"/>
                                  <div class="vpn-item__text">
                                      <span class="vpn-item__name">OpenVPN <button type="button" class="vpn-dl" @click="downloadFile(openVpnLink(item))"><v-icon icon="mdi-download" size="11"></v-icon>{{ t('myproxy.vpnDownload') }}</button><button type="button" class="vpn-dl" @click="showVpnQr(item, 'ps-ovpn')"><v-icon icon="mdi-qrcode" size="11"></v-icon>QR</button></span>
                                  </div>
                              </div>
                  </div>
                </div>
              </template>
              <!-- <template v-slot:[`item.auto_rotation_time`]="{ item }">
                  {{ item.auto_rotation_time <= 0 ? t('common.notDefined') : t('common.minutes', { n: item.auto_rotation_time }) }} </template> -->
                      <!-- Define other custom cell components using similar approach -->
          </v-data-table>
      </div>
      <div v-else-if="selectedView === 'Card'">
          <v-container style="padding:0px">
              <v-row  class="flex gap-5 pb-[30px]">
                  <v-col v-for="proxy in visibleProxies" :key="licenseUuid(proxy)" cols="auto" style="max-width:100%">
                      <v-card variant="" class="border" :class="{ 'card-expired': isExpired(proxy) }" rounded="0" width="400px" max-width="100%" height="100%" >
                          <!-- expired: stopped working; the login was re-locked, only Extend (and IP history) still work -->
                          <div v-if="isExpired(proxy)" class="expired-banner">
                              <div>
                                  <div class="expired-banner__title"><v-icon icon="mdi-alert" size="18"></v-icon>{{ t('myproxy.expiredTitle') }}</div>
                              </div>
                              <button type="button" class="expired-banner__btn" data-bs-toggle="modal" data-bs-target="#extendLicenseModal" @click="proxyAction(proxy, 'EXTEND')">{{ buttonLabel('EXTEND') }}</button>
                          </div>
                          <v-card-item>
                              <div class="flex flex-col gap-y-6">
                                  <div class="flex flex-col text-sm ">
                                    <div class="flex flex-row items-center">
                                        <span class="text-[16px] font-medium">{{ t('myproxy.note') }}</span>
                                        <v-icon v-if="proxy.note" icon="mdi-square-edit-outline"  class="cursor-pointer my-[1px] mx-4"
                                            data-bs-toggle="modal" color="light-green mx-1"
                                            data-bs-target="#noteModal"
                                            @click="proxyAction(proxy, 'UPDATENOTE')"
                                        ></v-icon>
                                        <v-icon v-else icon="mdi-plus-thick"  class="cursor-pointer my-[1px] mx-4"
                                            data-bs-toggle="modal" color="light-green mx-1"
                                            data-bs-target="#noteModal"
                                            @click="proxyAction(proxy, 'ADDNOTE')"
                                        ></v-icon>
                                    </div>
                                    <div v-if="proxy.note" class="flex justify-between items-center bg-white px-3 py-1 text-black">
                                        <pre  class=" text-green-600 w-full">{{proxy.note}}</pre>
                                    </div>
                                  </div>
                                  <div class="flex flex-row text-sm ">
                                    <span class="text-[16px] font-medium pb-1">{{ t('myproxy.couponCode') }}</span>
                                    <div class="pl-4 text-green-600">{{proxy?.coupon_code}}</div>
                                    <v-icon v-if="proxy?.coupon_code" icon="mdi-square-edit-outline"  class="cursor-pointer my-[1px] mx-4"
                                            data-bs-toggle="modal" color="light-green mx-1"
                                            data-bs-target="#couponModal"
                                            @click="proxyAction(proxy, 'EDITCOUPON')"
                                    ></v-icon>
                                    <v-icon v-else icon="mdi-plus-thick"  class="cursor-pointer my-[1px] mx-4"
                                        data-bs-toggle="modal" color="light-green mx-1"
                                        data-bs-target="#couponModal"
                                        @click="proxyAction(proxy, 'EDITCOUPON')"
                                    ></v-icon>
                                  </div>
                                  <div class="flex flex-col text-sm ">
                                    <span class="text-[16px] font-medium pb-1">{{ t('myproxy.serverStatus') }}</span>
                                    <div class="flex flex-wrap gap-2 justify-between py-1 font-semibold ">
                                      <div class="flex justify-between items-center bg-white px-3 py-1 text-black">
                                          {{ t('myproxy.serverOnline') }} <v-icon icon="mdi-circle" size="15"
                                              :color="(proxy.status_server_online ?? proxy.modem_conn_status) ? 'green' : 'red'" class="mr-2"></v-icon>
                                      </div>
                                      <div v-if="isExpired(proxy)" class="flex justify-between items-center bg-white px-3 py-1 text-red-600">
                                          {{ t('myproxy.proxyExpired') }} <v-icon icon="mdi-circle" size="15" color="red" class="mr-2"></v-icon>
                                      </div>
                                      <div v-else class="flex justify-between items-center bg-white px-3 py-1 text-black" :title="connsTitle(proxy)">
                                          {{ proxyStateText(proxy) }} <v-icon icon="mdi-circle" size="15"
                                              :color="shownStatus(proxy).color" class="mr-2"></v-icon>
                                      </div>
                                    </div>
                                  </div>
                                  <div class="flex flex-col text-sm ">
                                    <div v-if="outageOf(proxy)" class="outage-ban" :class="outageOf(proxy).state === 'outage' ? 'is-out' : 'is-ok'" role="status">
                      <v-icon :icon="outageOf(proxy).state === 'outage' ? 'mdi-lan-disconnect' : 'mdi-check-circle-outline'" size="18"></v-icon>
                      <span v-if="outageOf(proxy).state === 'outage'"><b>{{ t('outage.offlineTitle') }}</b> {{ t('outage.offlineText', { time: outageTime(outageOf(proxy).since) }) }}</span>
                      <span v-else><b>{{ t('outage.backTitle') }}</b> {{ t('outage.backText', { min: outageOf(proxy).minutes }) }}</span>
                  </div>
                                    <ProxyConnDetails :proxy="proxy" :expired="isExpired(proxy)" :copy-all="proxyDetails(proxy)" @edit-auth="proxyAction(proxy, 'AUTHENTICATION')" />
                                  </div>
                                  <!-- ProxySmart: OpenVPN file and QR code -->
                                  <div v-if="proxy.m_type === 'PROXYSMART' && proxy.openvpn_available !== false && !isExpired(proxy)" class="flex flex-row items-start text-sm">
                                    <span class="text-[16px] font-medium pr-3">VPN:</span>
                                    <div class="vpn-stack">
                              <div class="vpn-item" title="Download the OpenVPN file">
                                  <img :src="openVpnLogo" alt="OpenVPN" class="vpn-logo cursor-pointer" @click="downloadFile(openVpnLink(proxy))"/>
                                  <div class="vpn-item__text">
                                      <span class="vpn-item__name">OpenVPN <button type="button" class="vpn-dl" @click="downloadFile(openVpnLink(proxy))"><v-icon icon="mdi-download" size="11"></v-icon>{{ t('myproxy.vpnDownload') }}</button><button type="button" class="vpn-dl" @click="showVpnQr(proxy, 'ps-ovpn')"><v-icon icon="mdi-qrcode" size="11"></v-icon>QR</button></span>
                                  </div>
                              </div>
                                    </div>
                                  </div>
                                  <!-- ProProxyUI: WireGuard / OpenVPN files and whether they are connected -->
                                  <div v-if="proxy.m_type === 'PROPROXYUI' && (proxy.vpn_wg || proxy.vpn_ovpn) && !isExpired(proxy)" class="flex flex-row items-start text-sm">
                                    <span class="text-[16px] font-medium pr-3">VPN:</span>
                                    <div class="vpn-stack">
                              <div v-if="proxy.vpn_wg" class="vpn-item" title="Download the WireGuard file">
                                  <img :src="wireGuardLogo" alt="WireGuard" class="vpn-logo vpn-logo--wg cursor-pointer" @click="downloadVpn(proxy, 'wg')"/>
                                  <div class="vpn-item__text">
                                      <span class="vpn-item__name">WireGuard <button type="button" class="vpn-dl" @click="downloadVpn(proxy, 'wg')"><v-icon icon="mdi-download" size="11"></v-icon>{{ t('myproxy.vpnDownload') }}</button><button type="button" class="vpn-dl" @click="showVpnQr(proxy, 'wg')"><v-icon icon="mdi-qrcode" size="11"></v-icon>QR</button></span>
                                      <span v-if="proxy.vpn" class="vpn-state" :class="{ 'is-on': proxy.vpn.wg_connected }"><i></i>{{ proxy.vpn.wg_connected ? t('myproxy.vpnOn') : t('myproxy.vpnOff') }}</span>
                                  </div>
                              </div>
                              <div v-if="proxy.vpn_ovpn" class="vpn-item" title="Download the OpenVPN file">
                                  <img :src="openVpnLogo" alt="OpenVPN" class="vpn-logo cursor-pointer" @click="downloadVpn(proxy, 'ovpn')"/>
                                  <div class="vpn-item__text">
                                      <span class="vpn-item__name">OpenVPN <button type="button" class="vpn-dl" @click="downloadVpn(proxy, 'ovpn')"><v-icon icon="mdi-download" size="11"></v-icon>{{ t('myproxy.vpnDownload') }}</button><button type="button" class="vpn-dl" @click="showVpnQr(proxy, 'ovpn')"><v-icon icon="mdi-qrcode" size="11"></v-icon>QR</button></span>
                                      <span v-if="proxy.vpn" class="vpn-state" :class="{ 'is-on': proxy.vpn.ovpn_connected }"><i></i>{{ proxy.vpn.ovpn_connected ? t('myproxy.vpnOn') : t('myproxy.vpnOff') }}</span>
                                  </div>
                              </div>
                                    </div>
                                  </div>
                                  <div v-if="proxy.os_spoofing && osProfile(proxy)" class="flex flex-row items-center gap-2">
                                    <span class="text-[16px] font-medium pr-1">{{ t('myproxy.osSpoofing') }}</span>
                                    <span class="os-chip" :title="osDescription(proxy)"><v-icon icon="mdi-cellphone-link" size="14"></v-icon>{{ t('myproxy.osActive', { os: osProfile(proxy) }) }}{{ proxy.m_type === 'PROPROXYUI' && proxy.mtu ? ' · MTU ' + proxy.mtu : '' }}</span>
                                  </div>
                                  <div class="flex flex-row items-center justify-between">
                                    <div class="flex flex-row items-center">
                                      <span class="text-[16px] font-medium pr-3">{{ t('myproxy.autoExtend') }}</span>
                                      <div class="flex ">
                                        <v-switch density="dense" color="light-green" v-model="proxy.auto_extend" label-placement="left"
                                            label="" @click="updateAutoExtend(proxy)"></v-switch>
                                      </div>
                                    </div>
                                    <div>
                                      <span class="text-[16px] font-medium pr-3">{{ t('myproxy.rotationTime') }}</span>
                                      {{ proxy.auto_rotation_time <= 0 ? t('common.notDefined') : t('common.minutes', { n: proxy.auto_rotation_time }) }}
                                    </div>
                                  </div>
                                  <div class="flex flex-col">
                                    <div v-if="isExpired(proxy)" class="flex flex-row items-center">
                                        <span class="text-[18px] text-red-600 font-extrabold pr-5 min-w-[130px]">{{ t('myproxy.expiredLabel') }}</span>
                                        <div class="font-extrabold text-[18px] text-red-600">{{ formatDate(new Date(proxy.expired_date)) }}</div>
                                    </div>
                                    <div v-else-if="calculateRemainingTime(proxy.expired_date).isOne" class="flex flex-row items-center">
                                        <span class="text-[18px] text-red-600 font-extrabold pr-5 min-w-[130px]">{{ t('myproxy.remaining') }}</span>
                                        <div class="font-extrabold text-[18px] text-red-600">{{ calculateRemainingTime(proxy.expired_date).time }}</div>
                                    </div>
                                    <div v-else class="flex flex-row items-center">
                                        <span class="text-[18px] text-green-600 font-extrabold pr-5 min-w-[130px]">{{ t('myproxy.remaining') }}</span>
                                        <div class="font-extrabold text-[18px] text-green-600">{{ calculateRemainingTime(proxy.expired_date).time }}</div>
                                    </div>
                                    <div class="flex flex-row items-center">
                                      <span class="text-[16px] font-medium pr-5 min-w-[130px]">{{ t('myproxy.startTime') }}</span>
                                      <div>{{ formatDate(new Date(proxy.start_date)) }}</div>
                                    </div>
                                    <div class="flex flex-row items-center ">
                                      <span class="text-[16px] font-medium pr-5 min-w-[130px]">{{ t('myproxy.expireTime') }}</span>
                                      <div>{{ formatDate(new Date(proxy.expired_date)) }}</div>
                                    </div>
                                  </div>
                                  <div class="flex flex-col gap-3">
                                    <div
                                      v-if="!isIpv6Proxy(proxy)"
                                      class="flex flex-row items-center"
                                    >
                                      <span class="text-[16px] font-medium pr-5 min-w-[130px]">{{ t('myproxy.modemIpv4') }}</span>
                                      <div :class="ipStatus[proxy.uuid] ? 'text-light-green' : 'text-red'"
                                          class="font-bold flex justify-center items-center">
                                          <div>{{ proxy.public_ip ? proxy.public_ip : '* . * . * . * ' }}</div>
                                          <v-btn density="compact" variant="text" icon="mdi-refresh" color="light-green"
                                              @click="refreshIp(proxy.uuid)"></v-btn>
                                          <v-btn :disabled="!ipStatus[proxy.uuid]" class="font-bold bg-dark text-light ml-3 p-5" variant="outliend"
                                            style="outline:none; font-size: 12px;" density="compact" @click="changeIp(proxy.uuid)">
                                            {{ t('common.changeIp') }}
                                        </v-btn>
                                      </div>
                                    </div>
                                    <div v-if="isIpv6Proxy(proxy)" class="flex flex-row items-center">
                                      <span class="text-[16px] font-medium pr-5 min-w-[130px]">{{ t('myproxy.modemIpv6') }}</span>
                                      <div :class="ipStatus[proxy.uuid] ? 'text-light-green' : 'text-red'" class="font-bold break-all flex items-center">
                                        {{ proxy.public_ipv6 ? proxy.public_ipv6 : t('common.pendingSync') }}
                                        <v-btn density="compact" variant="text" icon="mdi-refresh" color="light-green"
                                            @click="refreshIp(proxy.uuid)"></v-btn>
                                        <v-btn :disabled="!ipStatus[proxy.uuid]" class="font-bold bg-dark text-light ml-3 p-5" variant="outliend"
                                          style="outline:none; font-size: 12px;" density="compact" @click="changeIp(proxy.uuid)">
                                          {{ t('common.changeIp') }}
                                        </v-btn>
                                      </div>
                                    </div>
                                    <div class="flex flex-row items-center ">
                                      <span class="text-[16px] font-medium pr-5 min-w-[130px]">{{ t('myproxy.download') }}</span>
                                      <div v-if="proxy.m_type === 'PROXYSMART'" :style="checkDownloadLimit(proxy, proxy.dl_bytes) ? 'color: red' : ''">
                                        {{ proxy.dl_bytes && proxy.dl_bytes !== "NaN" ? proxy.dl_bytes : '0MB'}} 
                                      </div>
                                      <div v-else-if="proxy.m_type === 'PROPROXYUI'" :style="ppOver(proxy) ? 'color: red' : ''">
                                          {{ t('myproxy.ppToday', { used: proxy.dl_bytes }) }}<span v-if="proxy.daily_gb > 0">&nbsp;{{ t('myproxy.ppOfGb', { gb: proxy.daily_gb }) }}</span>
                                      </div>
                                      <div v-else-if="proxy.m_type === 'X_PROXY' && proxy.m_variant === 'v2'" :style="checkDownloadLimit(proxy, proxy.dl_bytes) ? 'color: red' : ''">
                                          HTTP+SOCKS5: {{ proxy.dl_bytes }} MB
                                      </div>
                                      <div v-else-if="proxy.m_type === 'X_PROXY'">
                                          <div :style="checkDownloadLimit(proxy, proxy.dl_bytes) ? 'color: red' : ''">
                                              HTTP: 
                                              {{ proxy.dl_bytes }} MB
                                          </div>
                                          <div :style="checkDownloadLimit(proxy, (proxy.counter_dl_used_bytes/(1024 * 1024))) ? 'color: red' : ''">
                                              SOCKS5:  
                                              {{ (proxy.counter_dl_used_bytes / (1024 * 1024)).toFixed(2) }} MB
                                          </div>
                                      </div>
                                    </div>
                                  </div>

                              </div>
                          </v-card-item>

                          <v-card-actions>
                            <v-list class="flex gap-1 flex-wrap px-0 py-0  z-0 w-full customlist"
                            style="border-top-left-radius: 0px;border-top-right-radius: 0px">

                              <template v-for="(button, index) in buttonsFor(proxy)" :key="index">
                                  <!-- Check if button is "abc" -->

                                  <v-btn @click="proxyAction(proxy, button)" rounded="1"
                                      class="bg-dark text-light text-center ms-0 "
                                      :class="{ 'btn-extend': isExpired(proxy) && button === 'EXTEND', 'btn-os-on': button === 'OS Spoofing' && osProfile(proxy) }"
                                      :disabled="isExpired(proxy) && !ALLOWED_WHEN_EXPIRED.includes(button)"
                                      style="font-size: 10px; width: 120px; outline: none; border-bottom: 1px solid #345;"
                                      type="button"
                                      :data-bs-toggle="(button === 'AUTO ROTATION' || button === 'AUTHENTICATION' || button === 'EXTEND' || button === 'OS Spoofing' || button === 'IP HISTORY' || button === 'VPN' || button === 'VPN ✓') && 'modal'"
                                      :data-bs-target="button === 'AUTO ROTATION' ? '#autoRotationDialog' :
                                        button === 'AUTHENTICATION' ? '#authenticationModal' :
                                            button === 'EXTEND' ? '#extendLicenseModal' : button === 'OS Spoofing' ? '#osSpoofingModal' :
                                              button === 'IP HISTORY' ? '#ipChangeHistoryModal' : (button === 'VPN' || button === 'VPN ✓') ? '#vpnModal' :
                                                ''">
                                      {{ buttonLabel(button, proxy) }}
                                  </v-btn>
                                  
                              </template>
                            </v-list>
                          </v-card-actions>
                      </v-card>
                  </v-col>
              </v-row>
          </v-container>
      </div>

  </data>
  <div>
      <AutoRotationDialog :uuid="currentItem ? currentItem.uuid : null"
          :min_time_change_ip="currentItem ? currentItem.min_time_change_ip : null" :rotation_time="currentItem?.auto_rotation_time" @close="updatedHandler" />
      <AuthenticateDialog :license="currentItem ? currentItem : null" @close="updatedHandler" />
      <ExtendLicenseDialog :license="currentItem ? currentItem : null" @close="updatedHandler" />
      <OSSpoofingDialog :license="currentItem ? currentItem : null" @close="updatedHandler" />
      <VpnDialog :license="currentItem ? currentItem : null" @close="updatedHandler" />
      <IPChangeHistoryDialog :license="currentItem ? currentItem : null" />
      <!-- ProProxyUI: the WireGuard / OpenVPN QR code from the card -->
      <v-dialog :model-value="!!vpnQrPop" max-width="300" @update:model-value="(v) => { if (!v) vpnQrPop = null }">
        <div v-if="vpnQrPop" class="vpn-qr-pop">
          <b>{{ vpnQrPop.title }}</b>
          <img v-if="vpnQrPop.img" :src="vpnQrPop.img" alt="QR code" class="vpn-qr-pop__img"/>
          <div v-else class="vpn-qr-pop__img vpn-qr-pop__wait">{{ t('myproxy.qrLoading') }}</div>
          <div class="text-sm text-center">{{ vpnQrPop.text }}</div>
          <div class="text-xs text-center text-gray-500">{{ vpnQrPop.sub }}</div>
          <button type="button" class="vpn-dl" style="padding: 2px 14px;" @click="vpnQrPop = null">{{ t('myproxy.qrClose') }}</button>
        </div>
      </v-dialog>
      <NoteDialog :license="currentItem ? currentItem : null" @close="updatedHandler" />
      <CouponDialog :license="currentItem ? currentItem : null" @close="updatedHandler" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { UNDETECTABLE, PARTNERS_ON } from '@/helpers/partners';
import { t } from '@/i18n';
import AutoRotationDialog from "@/components/Customer/MyProxy/Modals/AutoRotationModal.vue"
import AuthenticateDialog from "@/components/Customer/MyProxy/Modals/AuthenticationModal.vue"
import NoteDialog from "@/components/Customer/MyProxy/Modals/NoteModal.vue"
import CouponDialog from "@/components/Customer/MyProxy/Modals/CouponModal.vue"
import ExtendLicenseDialog from "@/components/Customer/MyProxy/Modals/ExtendLicenseModal.vue"
import OSSpoofingDialog from "@/components/Customer/MyProxy/Modals/OSSpoofingModal.vue"
import VpnDialog from "@/components/Customer/MyProxy/Modals/VpnModal.vue"
import ProxyConnDetails from "@/components/Customer/MyProxy/ProxyConnDetails.vue"
import { proxiesCsv, downloadText, exportName, isActiveProxy } from '@/helpers/proxyExport';
import IPChangeHistoryDialog from "@/components/Customer/MyProxy/Modals/IPChangeHistoryModal.vue"
import { useAccountStore, useAlertStore, useProxyStore } from "@/stores"
import copy from "clipboard-copy"
// import CheckboxYes from "@/assets/check_yes_x.png"
// import CheckboxNo from "@/assets/check_no_x.png"
import { storeToRefs } from 'pinia';
import { watchEffect } from 'vue';
// import { connectToWebSocket } from "@/stores/socket.store"
import { proxyDisplayStatus } from '@/helpers/proxyDisplayStatus';
import { fetchWrapper } from '@/helpers';
import openVpnLogo from '@/assets/openVPN.png';
import wireGuardLogo from '@/assets/wireguard.svg';
import { wgQr, ovpnQr, linkQr } from '@/helpers/vpnQr';


let statusTimer;
let statusPollingStopped = false;
async function startStatusPolling() {
    try {
        if (!document.hidden) await proxyStore.refreshDisplayStatus();
    } finally {
        if (!statusPollingStopped) statusTimer = setTimeout(startStatusPolling, 5000);
    }
}
const selectedItems = ref([]);
const currentItem = ref(null)
const licenseStatusList = ref(['All', 'Active', 'Expired'])   // opens on All, so expired proxies are in view and can be extended
const viewList = ref(['Card', 'Table']);
// the values stay English (the filters compare against them); only what the customer reads is translated
const licenseStatusItems = computed(() => [{ title: t('myproxy.statusAll'), value: 'All' }, { title: t('myproxy.statusActive'), value: 'Active' }, { title: t('myproxy.statusExpired'), value: 'Expired' }]);
const viewItems = computed(() => [{ title: t('myproxy.viewCard'), value: 'Card' }, { title: t('myproxy.viewTable'), value: 'Table' }]);
// ProProxyUI: "Rotating" while this page's own Change IP runs (the box answers once the new IP is up)
const shownStatus = (p) => (p?.m_type === 'PROPROXYUI' && ipStatus.value[licenseUuid(p)] === false)
    ? { key: 'rotating', label: 'Rotating', color: 'amber-darken-2' } : proxyDisplayStatus(p);
// 'Proxy Online' / 'Proxy Rotating' / … in the chosen language
const proxyStateText = (p) => {
    const key = shownStatus(p).key;
    return key === 'maxconn' ? t('myproxy.maxConn') : t('myproxy.proxyState', { state: t('myproxy.states.' + key) });
};
// hover text on the status pill: live connections against the port's limit (ProxySmart only)
const connsTitle = (p) => (p && p.conns_total != null && p.conn_limit)
    ? t('myproxy.connsTitle', { total: p.conns_total, limit: p.conn_limit, http: p.conns_http ?? 0, socks: p.conns_socks5 ?? 0 })
    : null;
const buttonLabel = (b, p) => {
  if (b === 'OS Spoofing' && p?.m_type === 'PROPROXYUI') return t('myproxy.osMtuBtn');   // ProProxyUI: the window also sets the MTU; the active choice shows on the OS Spoofing line
  if ((b === 'VPN' || b === 'VPN ✓') && (p?.m_type === 'PROPROXYUI' || p?.m_type === 'PROXYSMART')) return t('myproxy.vpnQrBtn');   // ProProxyUI: the VPN window has the QR codes
  const k = 'myproxy.buttons.' + b; const out = t(k); return out === k ? b : out;
};
// active OS spoofing profile of a proxy ('' = off) and its description from the OS Spoofing window's list
const OS_NAMES = { 'android:1': 'p0f compliant but slow', 'android:3': 'real Android, almost like Linux', 'android:4': 'real Android 14', 'macosx:3': 'macOS X', 'macosx:4': 'real macOS 12.6 / iPhone 13 Pro Max', 'macosx:5': 'real macOS Ventura 13.5.1', 'ios:1': 'p0f compliant', 'ios:2': 'real iPhone', 'ios:3': 'real iPhone 12 Pro Max', 'windows:1': 'real Windows 10 64-bit Server or Tiny', 'windows:4': 'real Windows 10 and 11 64-bit Desktop' };
const osProfile = (p) => {
  if (p?.m_type === 'PROPROXYUI') return p.os_label || '';   // ProProxyUI: the box's name for the profile ('' = no spoofing)
  const v = String(p?.os ?? '').trim(); return v && v !== 'linux' ? v : '';
};
// ProProxyUI: today's data has reached the port's daily allowance
const ppOver = (p) => Number(p?.daily_gb) > 0 && Number(p?.dl_bytes) >= Number(p.daily_gb) * 1024;
const osDescription = (p) => OS_NAMES[osProfile(p)] || osProfile(p);
const expiringIn = ref(false);

const ipStatus = ref({})

const offsetHeight = 300; // Adjust this value as needed

const computedHeight = ref(window.innerHeight - offsetHeight);

const handleResize = () => {
computedHeight.value = window.innerHeight - offsetHeight;
};
const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;
const customApiUrl = `${process.env.VUE_APP_VUE_CUSTOM_API_URL || ''}`.replace(/\/$/, '');
const primaryBaseUrl = `${process.env.VUE_APP_VUE_BASEURL || ''}`.replace(/\/$/, '');
const configuredCustomApiBaseUrl = () => {
    if (!customApiUrl) return '';

    try {
        const url = new URL(customApiUrl, window.location.origin);
        return `${url.origin}${url.pathname.replace(/\/$/, '')}`;
    } catch {
        return customApiUrl;
    }
};
const publicApiBaseUrl = () => {
    try {
        const apiUrl = new URL(baseUrl, window.location.origin);
        const customBase = configuredCustomApiBaseUrl();
        const primaryOrigins = [apiUrl.origin];
        if (primaryBaseUrl) {
            primaryOrigins.push(new URL(primaryBaseUrl, window.location.origin).origin);
        }
        if (customBase && primaryOrigins.includes(window.location.origin)) {
            return customBase;
        }

        return `${window.location.origin}${apiUrl.pathname.replace(/\/$/, '')}`;
    } catch {
        return configuredCustomApiBaseUrl() || baseUrl.replace(/\/$/, '');
    }
};
const publicApiLink = (path, params) => {
    const url = new URL(`${publicApiBaseUrl()}/${path.replace(/^\/+/, '')}`);
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            url.searchParams.set(key, value);
        }
    });
    return url.toString();
};
const isEnabledFlag = (value) => value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
const isIpv6Proxy = (proxy) => isEnabledFlag(proxy?.is_ipv6) || (proxy?.m_type === 'PROXYSMART' && isEnabledFlag(proxy?.m_ipv6));
const licenseUuid = (proxy) => proxy?.license_uuid || proxy?.uuid || '';
// ProProxyUI: the QR button next to Download (WireGuard: the profile; OpenVPN: a 10-minute download link)
const vpnQrPop = ref(null);
const showVpnQr = async (proxy, type) => {
  if (type === 'ovpn' && !/^[^:\s]+:[^\s]+$/.test(String(proxy?.authentication_user || ''))) {
    useAlertStore().error('OpenVPN logs in with a username and password. Set a username:password login under Authentication first.');
    return;
  }
  const port = proxy?.http_port;
  vpnQrPop.value = type === 'wg'
    ? { title: `WireGuard · ${port}`, img: null, text: t('myproxy.qrWg'), sub: t('myproxy.qrWgSub') }
    : { title: `OpenVPN · ${port}`, img: null, text: t('myproxy.qrOvpn'), sub: t('myproxy.qrOvpnSub') };
  if (type === 'ps-ovpn') {   // ProxySmart: the profile's download link
    vpnQrPop.value = { title: `OpenVPN · ${port}`, img: null, text: t('myproxy.qrOvpn'), sub: t('myproxy.qrOvpnPsSub') };
    const ps = await linkQr(openVpnLink(proxy));
    if (vpnQrPop.value) vpnQrPop.value = { ...vpnQrPop.value, img: ps };
    return;
  }
  const img = type === 'wg' ? await wgQr(licenseUuid(proxy)) : await ovpnQr(licenseUuid(proxy));
  if (!img) { vpnQrPop.value = null; return; }
  if (vpnQrPop.value) vpnQrPop.value = { ...vpnQrPop.value, img };
};
// ProProxyUI: the WireGuard / OpenVPN logo on the card downloads that file
const downloadVpn = async (proxy, type) => {
  if (type === 'ovpn' && !/^[^:\s]+:[^\s]+$/.test(String(proxy?.authentication_user || ''))) {
    useAlertStore().error('OpenVPN logs in with a username and password. Set a username:password login under Authentication first.');
    return;
  }
  const f = await useProxyStore().vpnFile(licenseUuid(proxy), type);
  if (!f || !f.content) return;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([f.content], { type: 'application/octet-stream' }));
  a.download = f.filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
};
const linkHasCurrentParam = (link, param, value) => {
    if (!link || !value) return false;
    try {
        return new URL(link, window.location.origin).searchParams.get(param) === String(value);
    } catch {
        return false;
    }
};
const currentLicenseLink = (proxy, field, path, param) => {
    const uuid = licenseUuid(proxy);
    const existingLink = proxy?.[field];
    if (linkHasCurrentParam(existingLink, param, uuid)) {
        return existingLink;
    }
    return publicApiLink(path, { [param]: uuid });
};
const openVpnLink = (proxy) => currentLicenseLink(proxy, 'openvpn_link', 'openvpn', 'uuid');
const statusLink = (proxy) => currentLicenseLink(proxy, 'status_link', 'proxy/status', 'license');
const rotationLink = (proxy) => proxy?.rotation_link || publicApiLink('proxy/change-ip', { license: licenseUuid(proxy) });
const isOpenVpnLink = (fileUrl) => {
    try {
        const url = new URL(fileUrl, window.location.origin);
        return url.pathname.endsWith('/openvpn') && url.searchParams.has('uuid');
    } catch {
        return false;
    }
};
const downloadFile = async (fileUrl) => {
    const backendUrl = isOpenVpnLink(fileUrl)
        ? fileUrl
        : `${baseUrl}/account/download?url=${encodeURIComponent(fileUrl)}`;

    try {
    // Call the backend proxy with the dynamic URL as a query parameter

    const response = await fetch(backendUrl, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    // Get the file as a blob
    const blob = await response.blob();

    // Create a URL for the blob
    const blobUrl = window.URL.createObjectURL(blob);

    // Create a temporary <a> element to trigger the download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', 'vpn_profile.ovpn'); // Set the file name

    // Append the link to the body and trigger the click
    document.body.appendChild(link);
    link.click();

    // Clean up and remove the link
    document.body.removeChild(link);

    // Revoke the object URL to release memory
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading the file:', error);
  }
};

const checkDownloadLimit = (item, value) => {
    if(item.m_type==='X_PROXY') {
        if(parseFloat(item.counter_dl_limit_quota) !== 0 && item.counter_dl_limit_by?.toUpperCase() === 'DAILY' && parseFloat(item.counter_dl_limit_quota) <= parseFloat(value)) {
            return true
        }
        else return false
    } else {
        if(item.quota_dir === 'in' && item.quota_type === "daily" && value !== 0 && value !== '0') {
            const regex = /(\d+(\.\d+)?)\s*(\w+)/;
            const matches = value.match(regex);
            if(matches[3]?.toUpperCase() === 'MB' && parseFloat(matches[1]) >= parseFloat(item.band_amount)) return true
            else if(matches[3]?.toUpperCase() === 'GB' && (parseFloat(matches[1]) * 1024) >= parseFloat(item.band_amount)) return true
            else return false

        } else return false
    }
}

// VPN state of the XPROXY_NEW proxies, read from their server: shortly after the page opens and then every
// 10 minutes, so a VPN that was switched off on the server shows as off here and can be turned on again.
let vpnTimer = null, vpnFirst = null;
const refreshVpnStates = async () => {
  if (document.hidden) return;
  for (const item of (proxyList.value || []).filter((p) => p?.vpn_supported && String(p.status).toUpperCase() === 'ACTIVE')) {
    const state = await proxyStore.getVpnQuiet(licenseUuid(item));
    if (state && state.supported && item.vpn_enabled !== !!state.enabled) {
      item.vpn_enabled = !!state.enabled;
      proxyList.value = [...proxyList.value];   // the cards are built from a copy of this list
    }
  }
};

// outages ProxyMonitor reported on the servers of these proxies, refreshed every 2 minutes
const outages = ref({ licenses: {}, active_servers: 0 });
const loadOutages = async () => {
  if (document.hidden) return;
  try {
    const r = await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/outages/mine`);
    outages.value = { licenses: r.licenses || {}, active_servers: r.active_servers || 0 };
  } catch (_) { /* keep what we had */ }
};
const outageOf = (proxy) => outages.value.licenses[licenseUuid(proxy)] || null;
// the site-wide notice only shows to customers with an active proxy on a server that is down now
const hasProxyDown = computed(() => Object.values(outages.value.licenses).some((o) => o && o.state === 'outage'));
const outageTime = (v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
let outageTimer = null;

onMounted(() => {
loadOutages();
outageTimer = setInterval(loadOutages, 2 * 60 * 1000);
window.addEventListener('resize', handleResize);
startStatusPolling();
vpnFirst = setTimeout(refreshVpnStates, 8000);
vpnTimer = setInterval(refreshVpnStates, 10 * 60 * 1000);
});

onBeforeUnmount(() => {
clearInterval(outageTimer);
window.removeEventListener('resize', handleResize);
statusPollingStopped = true;
clearTimeout(statusTimer);
clearTimeout(vpnFirst);
clearInterval(vpnTimer);
});

const toggleSelection = () => {
    expiringIn.value = !expiringIn.value
};
const proxyStore = useProxyStore()
const proxyAction = (item, button) => {
  if (isExpired(item) && !ALLOWED_WHEN_EXPIRED.includes(button) && !['UPDATENOTE', 'EDITCOUPON', 'ADDNOTE'].includes(button)) return   // expired: only Extend, IP history and notes
  currentItem.value = item
  switch (button) {
      case "STATUS API":
          window.open(statusLink(item))
          break
      case "CHANGE IP (URL)":
          copyText(rotationLink(item))
          break
      case "AUTO ROTATION":
          // autoRotationDialog.value = true
          // alert()
          break
      case "REBOOT DEVICE":
          proxyStore.rebootDevice(item.uuid)
          break;
      case "AUTHENTICATION":
          break
      case "UPDATENOTE":
          break
      case "ADDNOTE":
          break
      case "EDITCOUPON":
          break
      case "EXTEND":
          // proxyStore.extendLicense(item.uuid)
          break
      case "OS Spoofing":
        break
      case "IP HISTORY":
        break
      case "GENERATE NEW URL":
        if (window.confirm(t('myproxy.regenConfirm'))) {
            proxyStore.updateLicense(licenseUuid(item))
        }
  }
}
const actionButtons = ref(
  ["CHANGE IP (URL)", "GENERATE NEW URL", "STATUS API", "IP HISTORY", "AUTO ROTATION", "REBOOT DEVICE", "EXTEND"]
)
const actionButtonsSmart = ref(
    ["CHANGE IP (URL)", "GENERATE NEW URL", "STATUS API", "IP HISTORY", "AUTO ROTATION", "REBOOT DEVICE", "EXTEND", "OS Spoofing"]
)
// an expired licence has stopped working and its login was re-locked: only these two buttons still do anything
const ALLOWED_WHEN_EXPIRED = ['EXTEND', 'IP HISTORY']
const isExpired = (p) => String(p?.status || '').toUpperCase() !== 'ACTIVE' || (p?.expired_date && new Date(p.expired_date).getTime() < Date.now())
const buttonsFor = (p) => {
  const list = [...actionButtons.value]
  if (p?.os_spoofing) list.push('OS Spoofing')
  if (p?.vpn_supported) list.push(p?.vpn_enabled ? 'VPN ✓' : 'VPN')
  else if (p?.m_type === 'PROXYSMART' && p?.openvpn_available !== false) list.push('VPN')   // ProxySmart: the OpenVPN window with its QR code
  return list
}
// Copy all: HTTP and SOCKS5 on their own lines, then the rotation link (same layout as the admin Licences copy)
// ProProxyUI box with no second port: one port answers HTTP and SOCKS5, shown as one line
const ppSinglePort = (p) => p?.m_type === 'PROPROXYUI' && Number(p?.socket_port) === Number(p?.http_port);
// Export: the customer's active proxies as a CSV file, made in the browser
const exportProxies = () => {
  const list = (proxyList.value || []).filter(isActiveProxy);
  if (!list.length) { useAlertStore().error(t('myproxy.exportNone')); return; }
  downloadText(exportName('', 'csv'), proxiesCsv(list, { rotationLink }), 'text/csv;charset=utf-8');
};
const proxyDetails = (item) => {
  const login = item.authentication_user ? ':' + item.authentication_user : ''
  return [
    ...(ppSinglePort(item) ? [`HTTP / SOCKS5: ${item.host_domain}:${item.http_port}${login}`] : [
    `HTTP: ${item.host_domain}:${item.http_port}${login}`,
    `SOCKS5: ${item.host_domain}:${item.socket_port}${login}`]),
    `Rotation link: ${rotationLink(item)}`,
  ].join('\n')
}
const copyText = (text) => {
  copy(text)
  const alertStore = useAlertStore()
  alertStore.success(t('common.copied'));
}

const accountStore = useAccountStore();
const { user } = storeToRefs(accountStore);

const headers = computed(() =>
  [
      { title: t('myproxy.h.no'), align: 'center', key: 'no' },
      { title: t('myproxy.h.note'), align: 'left', key: 'note'},
      { title: t('myproxy.h.coupon'), align: 'center', key: 'coupon_code' },
      { title: t('myproxy.h.status'), align: 'center', key: 'status' },
      { title: t('myproxy.h.ports'), align: 'center', key: 'proxy_ports', minWidth: "360", width: "360" },
      { title: t('myproxy.h.auth'), align: 'center', key: 'auth', minWidth: "200", width: "200" },
      { title: t('myproxy.h.autoExtend'), align: 'center', key: 'auto_extend', minWidth: "150", width: "150" },  
      // { title: 'Remaining', align: 'center', key: 'date', minWidth: "200", width: "200" },
      { title: t('myproxy.h.startExpire'), align: 'center', key: 'expired_date', minWidth: "200", width: "200" },
      { title: t('myproxy.h.ipDownload'), align: 'center', key: 'ip_status' },
      // { title: 'OpenVPN Connection File', align: 'center', key: 'openVPN'},
      // { title: 'Auto Rotation Time', align: 'center', key: 'auto_rotation_time' },
      { title: t('myproxy.h.actions'), align: 'center', key: 'buttonBar' },
  ]
);

const { proxyList, loading } = storeToRefs(proxyStore)
const numberedproxies = ref([])

const selectedLicenseStatus = ref("")
// what the table and the cards show: the chosen status (All = everything, active first, then the most recently
// expired), and only those expiring within a day when that box is ticked
const visibleProxies = computed(() => {
  const status = String(selectedLicenseStatus.value || 'All').toUpperCase()
  const list = numberedproxies.value.filter(item => (status === 'ALL' || String(item.status).toUpperCase() === status)
      && (!expiringIn.value || calculateRemainingTime(item.expired_date).isOne))
  if (status !== 'ALL') return list
  const active = (item) => String(item.status).toUpperCase() === 'ACTIVE'
  return [...list].sort((a, b) => (active(b) - active(a)) || (active(a) ? 0 : new Date(b.expired_date) - new Date(a.expired_date)))
})
const selectedView = ref("")
watchEffect(() => {
  if (user && user.value && user.value.user_name) {
      proxyStore.getProxyList()
    //   connectToWebSocket(user.value.user_name)
  }
}, [])
watchEffect(() => {

  numberedproxies.value = proxyList.value.map((item, index) => {
      const uuid = licenseUuid(item)
      if (ipStatus.value[uuid] === undefined)
          ipStatus.value = { ...ipStatus.value, [uuid]: true }
      return { ...item, no: index + 1, buttonBar: index }; // Add 'no' field with 1-based numbering
  })
}, [proxyList.value])
watchEffect(() => {
  selectedLicenseStatus.value = licenseStatusList.value[0]
  selectedView.value = viewList.value[0]
}, [licenseStatusList.value])
const changeIp = (uuid) => {
  ipStatus.value = {
      ...ipStatus.value,
      [uuid]: false
  }
  proxyStore.changeIp(uuid).then(() => {
      proxyStore.getProxyList()
      ipStatus.value = {
          ...ipStatus.value,
          [uuid]: true
      }
      setTimeout(() => {
          refreshIp(uuid);
      }, 2000);
      refreshIp(uuid);
  })
}

const refreshIp = (uuid) => {
  proxyStore.refreshIp(uuid).then(res => {
      if (res) {
          numberedproxies.value = numberedproxies.value.map(proxy => {
              if (proxy && proxy.uuid === uuid) {
                  const nextPublicIp = typeof res === "string" ? res : res.publicIp
                  const nextPublicIpv6 = typeof res === "string" ? null : res.publicIpv6
                  if (nextPublicIp) proxy.public_ip = nextPublicIp
                  if (typeof res !== "string" && Object.prototype.hasOwnProperty.call(res, "publicIpv6")) {
                      proxy.public_ipv6 = nextPublicIpv6
                  }
              }
              return proxy

          })
      }

  })
}

const updatedHandler = () => {
  proxyStore.getProxyList()
}
// The table renders copies of the store rows, so the switch must also update the store row itself;
// otherwise the next re-render rebuilds the copy from the stale store value and the switch snaps back.
const updateAutoExtend = (item) => {
  const targetFlag = !item.auto_extend
  const storeRow = proxyList.value.find(p => licenseUuid(p) === licenseUuid(item))
  if (storeRow) storeRow.auto_extend = targetFlag
  proxyStore.updateAutoExtend(item.uuid, targetFlag).then(res => {
      if (res === false && storeRow) storeRow.auto_extend = !targetFlag
      proxyStore.getProxyList()   // always re-read so the switch shows what the server stored
  })
}
const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year}  ${hours}:${minutes}:${seconds}`;
}
const calculateRemainingTime = (expiringTime) => {
    const now = new Date();
    const expirationDate = new Date(expiringTime);
    const timeDifference = expirationDate.getTime() - now.getTime();
    if(timeDifference<0){
        return t('common.expired')
    }
    // Convert milliseconds to days, hours, minutes, and seconds
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
    const str = (days ? t('myproxy.days', { n: days }) : '') +
        (hours ? t('myproxy.hours', { n: hours }) : '') +
        (minutes>=0 ? t('myproxy.mins', { n: minutes }) : '')
    return { time: str, isOne: days ? false : true }
}

// ---- recommended partner banner (closed with x: remembered in this browser for this account) ----
const partnerKey = computed(() => 'pp-hide-partner-undetectable-' + (user.value?.id || ''));
const partnerHidden = ref(false);
watchEffect(() => { try { partnerHidden.value = localStorage.getItem(partnerKey.value) === '1'; } catch (e) { partnerHidden.value = false; } });
const showPartner = computed(() => !partnerHidden.value && user.value?.role === 'user');
const hidePartner = () => { partnerHidden.value = true; try { localStorage.setItem(partnerKey.value, '1'); } catch (e) { /* storage blocked: hidden until reload */ } };
</script>
<style>
::-webkit-scrollbar {
  background: #78ffe7;
}
.card-expired { border: 2px solid #dc2626 !important; }
.expired-banner { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 14px; background: #dc2626; color: #ffffff; }
.expired-banner__title { display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 14px; }
.expired-banner__sub { font-size: 12px; opacity: 0.92; margin-top: 2px; }
.expired-banner .expired-banner__btn, .expired-banner .expired-banner__btn:hover, .expired-banner .expired-banner__btn:focus { flex: none; padding: 7px 16px; border: 2px solid #ffffff; border-radius: 6px; background: #ffffff !important; color: #b91c1c !important; font-weight: 800; font-size: 13px; letter-spacing: 0.03em; text-transform: uppercase; cursor: pointer; }
.expired-banner .expired-banner__btn:hover { background: #fee2e2 !important; }
.expired-tag { display: inline-block; padding: 3px 10px; border-radius: 4px; background: #dc2626; color: #ffffff; font-weight: 700; font-size: 12px; }
.btn-extend { background: #dc2626 !important; }
.os-chip { display: inline-flex; align-items: center; gap: 5px; padding: 2px 9px; border-radius: 6px; background: var(--ui-ok-soft); color: var(--ui-ok-ink); font-size: 13px; font-weight: 600; }
.os-chip--off { background: var(--ui-card-2); color: var(--ui-dim); font-weight: 400; }
.btn-os-on { background: var(--ui-ok) !important; color: #ffffff !important; }
</style>

<style scoped>
.partner-banner { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 14px; margin-bottom: 14px; padding: 12px 16px; border: 1px solid rgba(46, 196, 160, 0.45); border-radius: 12px; background: linear-gradient(90deg, rgba(46, 196, 160, 0.12), rgba(101, 96, 240, 0.1)); color: var(--ui-ink); }
.partner-banner__mark { display: grid; place-items: center; flex: none; width: 40px; height: 40px; border-radius: 10px; background: #0f1720; color: #2ec4a0; font-size: 15px; font-weight: 800; }
.partner-banner__txt { flex: 1; min-width: 220px; }
.partner-banner__txt b { display: block; font-size: 15px; }
.partner-banner__txt span { display: block; font-size: 13px; color: var(--ui-dim); }
.partner-banner__btn { flex: none; padding: 8px 14px; border-radius: 8px; background: #2ec4a0; color: #0b1b16 !important; font-size: 13.5px; font-weight: 700; text-decoration: none; }
.partner-banner__btn:hover { background: #3fd6b1; }
.partner-banner__x { flex: none; border: 0; background: transparent; color: var(--ui-dim); font-size: 20px; line-height: 1; cursor: pointer; }
</style>

<style scoped>
.outage-notice { display: flex; gap: 10px; align-items: flex-start; margin: 0 0 12px; padding: 10px 14px; border: 1px solid #8a3a3f; border-radius: 12px; background: #4a2a2f; color: #f6c9c9; font-size: 14px; }
.outage-ban { display: flex; gap: 8px; align-items: flex-start; margin: 0 0 8px; padding: 8px 10px; border-radius: 8px; font-size: 13px; line-height: 1.4; }
.outage-ban.is-out { border: 1px solid #8a3a3f; background: #4a2a2f; color: #f6c9c9; }
.outage-ban.is-ok { border: 1px solid #2f6b5f; background: #21403d; color: #9fe1cb; }
.copy-all-btn { background: var(--ui-pri) !important; color: #ffffff !important; text-transform: none; letter-spacing: 0; }
.vpn-logo { width: 26px; height: 26px; }
.udp-pill { display: inline-flex; align-items: center; gap: 2px; margin-left: 6px; padding: 0 7px; border-radius: 999px; font-size: 10.5px; line-height: 16px; vertical-align: 1px;
  color: #4ade80; background: rgba(34, 197, 94, 0.15); border: 1px solid rgba(34, 197, 94, 0.45); cursor: help; }
.vpn-qr-pop { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 16px; border-radius: 12px; background: var(--ui-card); color: var(--ui-ink); border: 1px solid var(--ui-line); }
.vpn-qr-pop__img { width: 210px; height: 210px; background: #ffffff; border-radius: 8px; padding: 6px; }
.vpn-qr-pop__wait { display: flex; align-items: center; justify-content: center; color: #555; font-size: 12px; }
.vpn-stack { display: flex; flex-direction: column; gap: 6px; margin: 1px 0; }
.vpn-item { display: flex; align-items: center; gap: 6px; }
.vpn-item__text { display: flex; flex-direction: column; line-height: 1.25; }
.vpn-item__name { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; color: #22c55e; white-space: nowrap; }
.vpn-dl { display: inline-flex; align-items: center; gap: 2px; padding: 0 6px; border: 1px solid #22c55e; border-radius: 6px; font-size: 10.5px; line-height: 16px; color: #22c55e; background: transparent; cursor: pointer; }
.vpn-dl:hover { background: rgba(34, 197, 94, 0.12); }
.vpn-state { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; white-space: nowrap; color: #ef4444; }
.vpn-state i { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #ef4444; }
.vpn-state.is-on { color: #22c55e; }
.vpn-state.is-on i { background: #22c55e; }
.vpn-logo--wg { background: #ffffff; border-radius: 50%; padding: 2px; }
</style>
